import { useCallback } from "react";
import { VITE_TENANTS_AND_SPACES_BASE_URL } from "../../../../env";
import request from "../../../request";

import backpackSdk from "@src/global_functions/backpackSdk/sdk";
import { skipToken, useQuery, useQueryClient } from "@tanstack/react-query";
import { recursiveCamelCaseCipher } from "../../../postgrestApi";
import { retryIfNot404 } from "../../reactQueryUtils";
import { tenantsAndSpacesKeys } from "../queryKeyFactory";
import { SpaceWithEnergyStarRequirements } from "../types";

type PropertyUseTypes =
  | "Office"
  | "Parking"
  | "MedicalOffice"
  | "Retail"
  | "Hotel"
  // The following are unsupported, and should not be returned for the office vertical rollup (happy path for initial property use release)
  // But the property use API technically can return these values.
  | "Other"
  | "OtherRetailMall"
  | "Restaurant"
  | "Education"
  | "DataCenter"
  // This won't be sent by the backend but is set as part of config to show incomplete state
  | "Incomplete";

export type PropertyUseDescription =
  | PropertyUseTypes
  | "OfficeVacant"
  | "MedicalOfficeVacant"
  | "ParkingUnpowered";

type PropertyUseBaseData = {
  description?: PropertyUseDescription;
  propertyUseType: PropertyUseTypes;
  name?: string;
  spaces: SpaceWithEnergyStarRequirements[];
};

type PropertyUseData = PropertyUseBaseData & {
  propertyUseAttributes: PropertyUseAttributes;
};
type ManualReviewData = PropertyUseBaseData & {
  propertyUseAttributes?: never;
};

export type PropertyUseOrManualReviewData = PropertyUseData | ManualReviewData;

export type PercentCooled = "MoreThan50" | "LessThan50" | "NotCooled";
type OfficePropertyUseAttributes = {
  grossFloorArea: number;
  numComputers: number;
  numWorkers: number;
  percentCooled: PercentCooled;
  workingHours: number;
};

type ParkingPropertyUseAttributes = {
  parkingOpen: number;
  parkingPartiallyEnclosed: number;
  parkingEnclosed: number;
  parkingHeating: boolean;
};

type RetailStorePropertyUseAttributes = {
  grossFloorArea: number;
  numWorkers: number;
  workingHours: number;
  hasExteriorEntrance: boolean;
  numChilledStorage: number;
  numWalkInChilledStorage: number;
  percentHeated: number;
  percentCooled: number;
  hasSingleStore: boolean;
  sellsGoods: boolean;
};

type PropertyUseAttributes = {
  Office?: OfficePropertyUseAttributes;
  Parking?: ParkingPropertyUseAttributes;
  RetailStore?: RetailStorePropertyUseAttributes;
};

export type PropertyUses = {
  propertyUses: PropertyUseData[];
  manualReview: ManualReviewData[];
};

const getPropertyUseRollup = async (
  siteId: number,
  buildingId: number
): Promise<PropertyUses> => {
  return request
    .get(
      `${VITE_TENANTS_AND_SPACES_BASE_URL}/sites/${siteId}/buildings/${buildingId}/property_use`
    )
    .query({ include: "spaces" })
    .then(({ body }) => recursiveCamelCaseCipher(body));
};

const PROPERTY_USE_ROLLUP_KEY = "getPropertyUseRollup";
export const useGetPropertyUseRollup = (siteId: number) => {
  const { data: building } =
    backpackSdk.tenantsAndSpaces.building.useQuery(siteId);

  return useQuery({
    queryKey: [
      ...tenantsAndSpacesKeys.query(
        siteId,
        "propertyUse",
        PROPERTY_USE_ROLLUP_KEY
      ),
      building?.id,
    ],
    queryFn:
      typeof building?.id === "number"
        ? () => getPropertyUseRollup(siteId, building.id)
        : skipToken,
    retry: retryIfNot404,
  });
};

export const useInvalidatePropertyUseRollup = (siteId: number) => {
  const client = useQueryClient();
  return useCallback(() => {
    client.invalidateQueries({
      queryKey: [
        ...tenantsAndSpacesKeys.query(
          siteId,
          "propertyUse",
          PROPERTY_USE_ROLLUP_KEY
        ),
      ],
    });
  }, [client, siteId]);
};
